// packages/contracts/src/bill-status.ts

/**
 * ★★★ WAVE 11 — THE `app.bill.status` DOMAIN, DECLARED ONCE.
 *
 * It was declared twice: `apps/officer-web/src/lib/types.ts`'s `BILL_STATUSES`
 * for the browser, and nothing at all on the server — which was survivable only
 * while no server code needed to know the domain. Wave 11's `?status=` filter
 * needs it, because a filter that must REFUSE a value outside the domain has to
 * know what the domain is, and the alternative was a second hand-written copy in
 * `services/billing`.
 *
 * ⚠ **A second copy is the S-011 shape and this repository has paid for it once**
 * (`PostgresBillRepository` carried a private `billScopePredicate`; the two
 * produced identical SQL, every behavioural test passed against both, and the
 * register's remedy was the weakest control available — an instruction to change
 * them together). Two copies of a status domain would be worse than a scope
 * predicate in one specific way: the drift is SILENT IN BOTH DIRECTIONS. A stale
 * server list turns a legitimately-stored status into a 400 on a screen that can
 * display it; a stale browser list turns a real status into an untileable row.
 *
 * So the domain lives here, in a kernel both a browser and a context may depend
 * on (ADR 0008: *"kernel — may depend on other kernels and on nothing else"*),
 * and everything else derives from it.
 *
 * ══ ⚠ WHAT PROVES THIS LIST IS RIGHT, because a constant cannot prove itself ══
 *
 * `apps/officer-web/src/lib/status-breakdown.test.ts` parses the domain out of
 * `infra/local/sql/` — from the LAST declaration in migration order, because
 * `060_bill_information_requested.sql` redeclares `003_domain.sql`'s CHECK — and
 * asserts it equals this list exactly. That suite also proves each redeclaration
 * is a SUPERSET of the one before it, so a migration that NARROWED the domain
 * fails there rather than here.
 *
 * ⚠ And the half no checked-in guard can see, restated because it is the reason
 * `breakdownOf` reports `unclassified` on screen: a DATABASE ahead of the build
 * reading it leaves every test green and the screen still wrong. That state is
 * covered in front of the officer, not in CI.
 *
 * ⚠ **ORDER IS THE DECLARED ORDER, not alphabetical**, and it is meaningful: it
 * is roughly the lifecycle a bill travels, which is the order a status `<select>`
 * should offer. Sorting it would make the filter control read as an arbitrary
 * list. Anything that needs a canonical order for COMPARISON sorts its own copy —
 * see `canonicalBillStatusFilter`.
 */
export const BILL_STATUS_VALUES = [
  'SUBMITTED',
  'AWAITING_L1',
  'AWAITING_L2',
  'APPROVED',
  'REJECTED',
  /** ★ Wave 10, migration 060 — Annex A 37/38's pause pending information from the provider. */
  'INFORMATION_REQUESTED',
] as const

export type BillStatusValue = (typeof BILL_STATUS_VALUES)[number]

/** Whether an untrusted string is a status this product stores. */
export const isBillStatus = (value: string): value is BillStatusValue =>
  (BILL_STATUS_VALUES as readonly string[]).includes(value)

/**
 * ★★★ WAVE 27 — the three bill types, as a VALUE rather than only a union type.
 *
 * ⚠ **The union `'NEW' | 'UPWARD_ADJUSTMENT' | 'DOWNWARD_ADJUSTMENT'` already
 * existed in four places and could not be iterated.** A type vanishes at runtime,
 * so a route validating `?billType=` against it had nothing to check — the choice
 * was a hand-written array beside the type (a second copy, free to drift) or this.
 *
 * ⚠ **ORDER IS THE ORDER A CONTROL SHOULD OFFER**, exactly as
 * {@link BILL_STATUS_VALUES} records for itself: NEW first because it is the
 * overwhelming majority, then the two adjustments. Anything needing a canonical
 * order for COMPARISON sorts its own copy.
 */
export const BILL_TYPE_VALUES = ['NEW', 'UPWARD_ADJUSTMENT', 'DOWNWARD_ADJUSTMENT'] as const

export type BillType = (typeof BILL_TYPE_VALUES)[number]
